/**
 * AccessTab — onglet « Accès aux tuiles » de Portail clients (ex-Gestion des Accès).
 * Matrice de tuiles visibles pour chaque dossier Master (Principal + Secondaire),
 * auto-sélection via ?master_id=… (liens depuis Paie, fiche Master, onglet Comptes).
 */
import { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { Loader2, Search, Users, Lock } from "lucide-react";
import PortalPermissionsPanel from "../masters/PortalPermissionsPanel";
import { toast } from "@/lib/toast";
import { API, authH } from "@/api";


export default function AccessTab({ masters = [] }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialMasterId = searchParams.get("master_id") || "";

  const [search, setSearch] = useState("");
  const [selectedMasterId, setSelectedMasterId] = useState(initialMasterId);
  const [masterDetail, setMasterDetail] = useState(null);
  const loadingList = false;
  const [loadingDetail, setLoadingDetail] = useState(false);

  // Suit un changement de ?master_id= (passerelle depuis l'onglet Comptes)
  useEffect(() => { if (initialMasterId) setSelectedMasterId(initialMasterId); }, [initialMasterId]);

  // Charge le détail du master sélectionné
  useEffect(() => {
    if (!selectedMasterId) { setMasterDetail(null); return; }
    setLoadingDetail(true);
    (async () => {
      try {
        const r = await axios.get(`${API}/masters/${selectedMasterId}`, { headers: authH() });
        setMasterDetail(r.data);
        setSearchParams({ tab: "acces", master_id: selectedMasterId }, { replace: true });
      } catch (e) {
        toast.error(e.response?.data?.detail || "Erreur");
        setMasterDetail(null);
      } finally {
        setLoadingDetail(false);
      }
    })();
  }, [selectedMasterId, setSearchParams]);

  const filtered = useMemo(() => {
    if (!search.trim()) return masters;
    const s = search.toLowerCase();
    return masters.filter((m) =>
      (m.couple_display_name || "").toLowerCase().includes(s) ||
      (m.primary_email || "").toLowerCase().includes(s) ||
      (m.primary_name || "").toLowerCase().includes(s) ||
      (m.secondary_name || "").toLowerCase().includes(s)
    );
  }, [masters, search]);

  return (
    <div data-testid="gestion-acces-page">
      <p className="text-sm text-slate-500">
        Configurez les tuiles visibles sur le portail client pour chaque dossier — mode brouillon avant publication.
      </p>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 mt-4">
        {/* LISTE des Masters */}
        <aside className="lg:col-span-4 bg-white border border-slate-200 rounded-xl overflow-hidden flex flex-col" data-testid="gestion-acces-master-list">
          <div className="px-3 py-2.5 border-b border-slate-100 bg-slate-50">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-2.5 top-2 text-slate-400" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Rechercher un dossier…"
                className="pl-8 pr-3 py-1.5 border rounded-lg text-sm w-full bg-white"
                data-testid="gestion-acces-search"
              />
            </div>
          </div>
          <div className="flex-1 overflow-y-auto max-h-[60vh]">
            {loadingList ? (
              <div className="text-center py-8"><Loader2 className="inline w-5 h-5 animate-spin text-slate-400" /></div>
            ) : filtered.length === 0 ? (
              <div className="text-center py-8 text-sm text-slate-400 italic">
                {masters.length === 0
                  ? <>Aucun dossier Master créé.<br /><Link to="/admin/masters" className="text-indigo-600 underline">Créer un dossier →</Link></>
                  : "Aucun résultat"}
              </div>
            ) : (
              filtered.map((m) => (
                <button
                  key={m.id}
                  onClick={() => setSelectedMasterId(m.id)}
                  className={`w-full text-left px-3 py-2.5 border-b border-slate-50 transition ${
                    selectedMasterId === m.id ? "bg-rose-50 border-l-4 border-l-rose-500" : "hover:bg-slate-50"
                  }`}
                  data-testid={`gestion-acces-row-${m.id}`}
                >
                  <div className="font-semibold text-sm text-slate-800 truncate inline-flex items-center gap-1.5 max-w-full">
                    <span className="truncate">{m.couple_display_name || "(sans nom)"}</span>
                    {!Object.values(m.portal_permissions_primary || {}).some((v) => v === true) && (
                      <span
                        className="shrink-0 text-[9px] font-semibold bg-rose-100 text-rose-700 px-1.5 py-0.5 rounded border border-rose-200"
                        title="Aucune tuile cochée pour le compte principal"
                        data-testid={`gestion-acces-empty-${m.id}`}
                      >
                        0 tuile
                      </span>
                    )}
                  </div>
                  <div className="text-[11px] text-slate-500 inline-flex items-center gap-1 mt-0.5">
                    <Users className="w-3 h-3" />{m.primary_name || "?"}{m.secondary_name ? ` + ${m.secondary_name}` : ""}
                  </div>
                </button>
              ))
            )}
          </div>
        </aside>

        {/* PANEL DE GESTION */}
        <main className="lg:col-span-8">
          {!selectedMasterId ? (
            <div className="bg-white border border-dashed border-slate-300 rounded-xl p-12 text-center" data-testid="gestion-acces-empty">
              <Lock className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <h3 className="font-semibold text-slate-700">Sélectionnez un dossier</h3>
              <p className="text-sm text-slate-500 mt-1">
                Choisissez un dossier Master dans la liste pour configurer les tuiles visibles sur son portail client.
              </p>
            </div>
          ) : loadingDetail || !masterDetail ? (
            <div className="bg-white border border-slate-200 rounded-xl p-12 text-center">
              <Loader2 className="w-7 h-7 animate-spin text-slate-400 mx-auto" />
            </div>
          ) : (
            <div className="space-y-4">
              <div className="bg-gradient-to-br from-rose-50 to-amber-50 border border-rose-100 rounded-xl p-4">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="text-[11px] uppercase tracking-wider text-rose-700 font-semibold">Dossier sélectionné</div>
                    <h2 className="text-xl font-bold text-slate-800 mt-0.5">{masterDetail.couple_display_name}</h2>
                    <div className="text-[11px] text-slate-500 mt-1 inline-flex items-center gap-1">
                      <Users className="w-3 h-3" />
                      {masterDetail.primary_user?.first_name} {masterDetail.primary_user?.last_name}
                      {masterDetail.secondary_user && (
                        <> + {masterDetail.secondary_user?.first_name} {masterDetail.secondary_user?.last_name}</>
                      )}
                    </div>
                  </div>
                  <Link
                    to={`/admin/masters/${masterDetail.id}`}
                    className="text-xs px-3 py-1.5 rounded-lg bg-white border border-slate-200 hover:bg-slate-50 text-slate-700"
                    data-testid="gestion-acces-go-master"
                  >
                    Voir la fiche →
                  </Link>
                </div>

                {/* Batch #13.1 — Toggle global d'accès au portail pour le secondaire */}
                {masterDetail.secondary_user_id && (
                  <div className="mt-3 pt-3 border-t border-rose-200/60 flex items-center justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-slate-800">
                        Accès portail du conjoint
                      </div>
                      <div className="text-[11px] text-slate-600 mt-0.5">
                        {masterDetail.secondary_portal_enabled
                          ? "Le conjoint peut se connecter au portail avec ses propres identifiants."
                          : "Le conjoint est bloqué à la connexion (matrice de tuiles ignorée tant que ce toggle est OFF)."}
                      </div>
                    </div>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={!!masterDetail.secondary_portal_enabled}
                      onClick={async () => {
                        const newVal = !masterDetail.secondary_portal_enabled;
                        try {
                          const r = await axios.put(
                            `${API}/masters/${masterDetail.id}`,
                            { secondary_portal_enabled: newVal },
                            { headers: authH() },
                          );
                          setMasterDetail(r.data);
                          toast.success(newVal ? "Accès du conjoint activé" : "Accès du conjoint désactivé");
                        } catch (e) {
                          toast.error(e.response?.data?.detail || "Erreur mise à jour");
                        }
                      }}
                      className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition ${
                        masterDetail.secondary_portal_enabled ? "bg-emerald-600" : "bg-slate-300"
                      }`}
                      data-testid="gestion-acces-secondary-portal-toggle"
                    >
                      <span
                        aria-hidden="true"
                        className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition ${
                          masterDetail.secondary_portal_enabled ? "translate-x-5" : "translate-x-0"
                        }`}
                      />
                    </button>
                  </div>
                )}
              </div>

              {/* Refonte « Tout ou Rien » — Panel unique de gestion des tuiles portail */}
              <PortalPermissionsPanel master={masterDetail} />
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
